import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    Chip,
} from "@mui/material";
import { Product } from "../../types";
import { productService } from "../../services/productService";
import { ProductThumbnail } from "./ProductThumbnail";

interface ProductSearchAutocompleteProps<Multiple extends boolean | undefined> {
    value?: Multiple extends true ? Product[] : Product | null;
    onChange: (
        product: Multiple extends true ? Product[] : Product | null,
    ) => void;
    multiple?: Multiple;
    label?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    sx?: any;
    showStock?: boolean;
    // Second line shown under each option's name. Defaults to "sku" so
    // every existing caller (Order form, advanced search filters, product
    // merge) is unaffected — procurement opts into "stock" since suppliers
    // care about current stock levels, not the SKU, when choosing what to
    // reorder.
    secondaryLine?: "sku" | "stock";
    // Seeds the search box so results for a known term (e.g. an unresolved
    // SKU/name from the order-import flow) load without the user typing
    // anything first. Every existing caller omits this, so their search
    // box still starts blank exactly as before.
    initialSearchTerm?: string;
}

export const ProductSearchAutocomplete = <
    Multiple extends boolean | undefined = false,
>({
    value,
    onChange,
    multiple = false as Multiple,
    label = "Product",
    required = false,
    error = false,
    helperText,
    disabled = false,
    sx,
    showStock = false,
    secondaryLine = "sku",
    initialSearchTerm,
}: ProductSearchAutocompleteProps<Multiple>) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? "");
    const [open, setOpen] = useState(false);
    const [selectedProductDetails, setSelectedProductDetails] = useState<
        Product | Product[] | null
    >(null);

    const abortControllerRef = useRef<AbortController | null>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Constants
    const DEBOUNCE_DELAY = 1000; // Reduced to 1 second for faster search
    const INITIAL_LOAD_LIMIT = 50; // Load first 50 products initially
    const SEARCH_MIN_LENGTH = 1; // Start searching after 1 character

    // Fetch product details when value changes (for displaying already selected products)
    useEffect(() => {
        if (!value) {
            setSelectedProductDetails(null);
            return;
        }

        if (multiple && Array.isArray(value)) {
            // For multiple selection, we rely on the parent to provide full objects or we accept partials
            // Fetching all details for an array would be complex here, assuming parent manages full objects
            setSelectedProductDetails(value);
            return;
        }

        if (
            !multiple &&
            !Array.isArray(value) &&
            (value as Product)?.product_id
        ) {
            const singleVal = value as Product;
            // Prefer showing the provided value immediately
            setSelectedProductDetails((prev) => {
                if (Array.isArray(prev)) return singleVal;
                return prev?.product_id === singleVal.product_id
                    ? prev
                    : singleVal;
            });

            // Fetch full details in background
            productService
                .getProductById(singleVal.product_id)
                .then((product: Product) => {
                    setSelectedProductDetails(product);
                })
                .catch((err: any) => {
                    console.error("Failed to fetch product details:", err);
                });
        }
    }, [value, multiple]);

    const fetchProducts = useCallback(
        async (
            search: string = "",
            skip: number = 0,
            limit: number = INITIAL_LOAD_LIMIT,
        ) => {
            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller
            abortControllerRef.current = new AbortController();

            try {
                setLoading(true);

                const response = await productService.getProducts({
                    skip,
                    limit,
                    ...(search && { search }),
                });

                // Only update if this request wasn't aborted
                if (!abortControllerRef.current?.signal.aborted) {
                    const items = response?.data || [];
                    setProducts(items);
                }
            } catch (err: any) {
                if (err.name !== "AbortError") {
                    console.error("Failed to fetch products:", err);
                }
            } finally {
                if (!abortControllerRef.current?.signal.aborted) {
                    setLoading(false);
                }
            }
        },
        [],
    );

    // Initial load - fetch first page of products
    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Debounced search effect
    useEffect(() => {
        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // If search term is empty, load initial products
        if (!searchTerm) {
            fetchProducts();
            return;
        }

        // If search term is too short, don't search yet
        if (searchTerm.length < SEARCH_MIN_LENGTH) {
            return;
        }

        // Set up debounced search
        debounceTimerRef.current = setTimeout(() => {
            fetchProducts(searchTerm, 0, INITIAL_LOAD_LIMIT);
        }, DEBOUNCE_DELAY);

        // Cleanup function
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchTerm, fetchProducts]);

    const handleInputChange = useCallback(
        (event: React.SyntheticEvent, newInputValue: string) => {
            setSearchTerm(newInputValue);
        },
        [],
    );

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleChange = useCallback(
        (event: React.SyntheticEvent, newValue: any) => {
            setSelectedProductDetails(newValue);
            onChange(newValue);
        },
        [onChange],
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Get the display value for the autocomplete
    const getDisplayValue = () => {
        if (selectedProductDetails) return selectedProductDetails as any;
        if (value) return value as any;
        return multiple ? [] : null;
    };

    // Ensure products is always an array
    const safeProducts = Array.isArray(products) ? products : [];

    const displayValue = getDisplayValue();
    // Native title tooltip for the selected product's full name — a
    // narrow container (e.g. a table cell) can still clip a long name
    // even after giving the field room to grow, so this is the fallback
    // that always works regardless of width. Single-select only; multiple
    // mode already shows each pick as its own chip, so there's no single
    // truncated value to explain.
    const selectedTitle =
        !multiple && displayValue && !Array.isArray(displayValue)
            ? (displayValue as Product).name
            : undefined;

    return (
        <Autocomplete
            multiple={multiple}
            value={displayValue}
            inputValue={searchTerm}
            onChange={handleChange}
            onInputChange={handleInputChange}
            options={safeProducts}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, val) =>
                Boolean(option && val && option.product_id === val.product_id)
            }
            loading={loading}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            disabled={disabled}
            sx={sx}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    inputProps={{ ...params.inputProps, title: selectedTitle }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                {loading && (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                )}
                                {params.InputProps.endAdornment}
                            </Box>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                // Total on-hand and available-to-sell quantities — computed
                // whenever stock data is present, independent of showStock
                // (which only gates the small in-stock/out-of-stock badge
                // next to the name; secondaryLine="stock" below uses these
                // same numbers for the subtitle line).
                const totalStock = option.stock ? option.stock.quantity : null;
                const availableStockCount = option.stock
                    ? Math.max(
                          0,
                          option.stock.quantity -
                              (option.stock.reserved_quantity || 0),
                      )
                    : null;

                // Calculate available stock badge if showStock is enabled
                const availableStock = showStock ? availableStockCount : null;
                const isLowStock =
                    availableStock !== null &&
                    availableStock > 0 &&
                    availableStock <= 5;
                const isOutOfStock =
                    availableStock !== null && availableStock <= 0;

                // A plain string normally, but a was/now pair while the
                // product is actually on sale — interpolated as {priceLabel}
                // below either way, so callers don't need to change.
                const priceLabel = !option.selling_price ? (
                    "Price not set"
                ) : option.is_on_sale && option.effective_price != null ? (
                    <>
                        <Box
                            component="span"
                            sx={{
                                textDecoration: "line-through",
                                color: "text.disabled",
                                mr: 0.5,
                            }}
                        >
                            ৳{option.selling_price}
                        </Box>
                        <Box
                            component="span"
                            sx={{ color: "success.main", fontWeight: 600 }}
                        >
                            ৳{option.effective_price}
                        </Box>
                    </>
                ) : (
                    `৳${option.selling_price}`
                );

                return (
                    <Box
                        component="li"
                        {...props}
                        key={option.product_id}
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                        <ProductThumbnail images={option.images} alt={option.name} />
                        <Box sx={{ width: "100%" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    fontWeight: "bold",
                                }}
                            >
                                {option.name}
                                {showStock && availableStock !== null && (
                                    <Chip
                                        label={
                                            isOutOfStock
                                                ? "Out of Stock"
                                                : `${availableStock} in stock`
                                        }
                                        size="small"
                                        color={
                                            isOutOfStock
                                                ? "error"
                                                : isLowStock
                                                  ? "warning"
                                                  : "success"
                                        }
                                        variant="outlined"
                                        sx={{
                                            height: 20,
                                            fontSize: "0.7rem",
                                            fontWeight: 500,
                                        }}
                                    />
                                )}
                            </Box>
                            <Box
                                sx={{
                                    fontSize: "0.875rem",
                                    color: "text.secondary",
                                }}
                            >
                                {secondaryLine === "stock" ? (
                                    totalStock !== null ? (
                                        <>
                                            Total: {totalStock} • Available:{" "}
                                            {availableStockCount} •{" "}
                                            {priceLabel}
                                        </>
                                    ) : (
                                        <>
                                            Stock data unavailable •{" "}
                                            {priceLabel}
                                        </>
                                    )
                                ) : (
                                    <>
                                        SKU: {option.sku} • {priceLabel}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                );
            }}
            filterOptions={(options) => (Array.isArray(options) ? options : [])} // We handle filtering server-side
            noOptionsText={
                searchTerm.length < SEARCH_MIN_LENGTH
                    ? `Type at least ${SEARCH_MIN_LENGTH} character${
                          SEARCH_MIN_LENGTH > 1 ? "s" : ""
                      } to search`
                    : loading
                      ? "Searching products..."
                      : "No products found"
            }
        />
    );
};
